/**
 * YAML frontmatter parsing and serialization utilities
 */

import matter from 'gray-matter';
import * as yaml from 'js-yaml';
import type { FileMetadata, FileType } from '@phrasewright/types';

// Result of parsing frontmatter
export interface ParsedFrontmatter {
  /** Extracted metadata */
  metadata: FileMetadata;
  /** Content without frontmatter */
  content: string;
  /** Original content with frontmatter */
  rawContent: string;
  /** Whether frontmatter was present */
  hasFrontmatter: boolean;
  /** Any parsing errors encountered */
  errors: string[];
  /** Any warnings encountered */
  warnings: string[];
  /** Parsed suggestion directives */
  suggestionDirectives: SuggestionDirectives;
}

// Options for frontmatter parsing
export interface FrontmatterOptions {
  /** Default file type if not specified */
  defaultType?: FileType;
  /** Whether to extract title from content if not in frontmatter */
  extractTitle?: boolean;
  /** Whether to auto-generate updated timestamp */
  autoUpdateTimestamp?: boolean;
  /** Custom field mapping */
  fieldMapping?: Record<string, string>;
  /** Custom delimiters for frontmatter */
  delimiters?: string | string[];
  /** Strict mode for validation */
  strict?: boolean;
  /** Validate types */
  validateTypes?: boolean;
  /** Merge with existing metadata */
  merge?: boolean;
}

// ============================================================================
// Suggestion Directive Types
// ============================================================================

/**
 * Special frontmatter directives that control phrase suggestion behavior
 */
export interface SuggestionDirectives {
  /** Tag-based phrase inclusion directives */
  includeFrom?: TagIncludeDirective[];
  /** Theme filtering directives */
  theme?: ThemeDirective;
  /** Audience targeting directives */
  audience?: AudienceDirective;
  /** Style preference directives */
  style?: StyleDirective;
  /** Exclusion directives */
  excludeFrom?: TagExcludeDirective[];
}

/**
 * Tag-based inclusion directive for phrase suggestions
 * Format: @include-from: tag1,tag2 [weight: high] [priority: 1]
 */
export interface TagIncludeDirective {
  /** Tags to include phrases from */
  tags: string[];
  /** Weight/importance of these tags (low, medium, high, critical) */
  weight: 'low' | 'medium' | 'high' | 'critical';
  /** Priority order (lower number = higher priority) */
  priority: number;
  /** Whether to require ALL tags or ANY tag */
  mode: 'all' | 'any';
  /** Optional contextual filter */
  context?: string;
}

/**
 * Tag-based exclusion directive for phrase suggestions
 * Format: @exclude-from: tag1,tag2 [reason: context]
 */
export interface TagExcludeDirective {
  /** Tags to exclude phrases from */
  tags: string[];
  /** Reason for exclusion (for debugging/transparency) */
  reason?: string;
  /** Whether exclusion is strict (hard) or soft (deprioritize) */
  mode: 'hard' | 'soft';
}

/**
 * Theme filtering directive for contextual suggestions
 * Format: @theme: technical,documentation [boost: 0.8]
 */
export interface ThemeDirective {
  /** Primary themes for the document */
  themes: string[];
  /** Boost multiplier for theme-matching phrases (0.1-2.0) */
  boost: number;
  /** Theme specificity (broad, focused, strict) */
  specificity: 'broad' | 'focused' | 'strict';
  /** Sub-themes or categories */
  subThemes?: string[];
}

/**
 * Audience targeting directive for tone and complexity adjustment
 * Format: @audience: developer,intermediate [complexity: medium] [tone: professional]
 */
export interface AudienceDirective {
  /** Target audience types */
  audienceTypes: string[];
  /** Content complexity level */
  complexity: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  /** Desired tone */
  tone: 'formal' | 'casual' | 'friendly' | 'professional' | 'technical';
  /** Domain expertise level */
  domainExpertise?: string;
  /** Language formality preference */
  formality: 'high' | 'medium' | 'low';
}

/**
 * Style preference directive for format and presentation
 * Format: @style: concise,structured [format: bullets] [voice: active]
 */
export interface StyleDirective {
  /** Style characteristics */
  characteristics: string[];
  /** Preferred format */
  format: 'prose' | 'bullets' | 'structured' | 'narrative' | 'instructional';
  /** Voice preference */
  voice: 'active' | 'passive' | 'mixed';
  /** Length preference */
  lengthPreference: 'concise' | 'moderate' | 'detailed';
  /** Structural preferences */
  structure?: {
    useHeadings: boolean;
    useBullets: boolean;
    useNumbering: boolean;
    useExamples: boolean;
  };
}

// Default parsing options
const DEFAULT_OPTIONS: Required<FrontmatterOptions> = {
  defaultType: 'document',
  extractTitle: true,
  autoUpdateTimestamp: true,
  fieldMapping: {},
  delimiters: '---',
  strict: false,
  validateTypes: true,
  merge: false,
};

/**
 * Parse YAML frontmatter from content
 */
export function parseFrontmatter(
  content: string,
  options: FrontmatterOptions = {}
): ParsedFrontmatter {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const warnings: string[] = [];

  try {
    // Parse with gray-matter library with custom delimiters if provided
    const matterOptions: any = {};
    if (opts.delimiters) {
      matterOptions.delimiters = opts.delimiters;
    }
    const parsed = matter(content, matterOptions);
    const attributes = parsed.data as Record<string, any>;

    // Check if we actually have frontmatter
    // Empty frontmatter (just delimiters) still counts as having frontmatter
    const actuallyHasFrontmatter =
      content
        .trimStart()
        .startsWith(
          Array.isArray(opts.delimiters)
            ? opts.delimiters[0]
            : opts.delimiters || '---'
        ) || content.trimStart().startsWith('---');

    // Extract and validate metadata
    let metadata: FileMetadata;

    // If frontmatter is empty (just delimiters), return empty metadata
    if (actuallyHasFrontmatter && Object.keys(attributes).length === 0) {
      metadata = {} as FileMetadata;
    } else {
      metadata = {
        title: extractTitle(
          attributes.title,
          parsed.content,
          opts.extractTitle
        ),
        type: validateFileType(attributes.type, opts.defaultType),
        tags: normalizeTags(attributes.tags),
        updated: normalizeTimestamp(
          attributes.updated,
          opts.autoUpdateTimestamp
        ),
        // If there's a specific 'custom' field in the YAML, use that; otherwise extract all non-standard fields
        custom: attributes.custom || extractCustomFields(attributes),
      };

      // Add description if present (for multiline values test)
      if (attributes.description) {
        if (!metadata.custom) metadata.custom = {};
        metadata.custom.description = attributes.description;
      }

      // Add any non-standard fields to metadata directly (for test compatibility)
      Object.keys(attributes).forEach((key) => {
        if (!['title', 'type', 'tags', 'updated', 'custom'].includes(key)) {
          (metadata as any)[key] = attributes[key];
        }
      });
    }

    // Validate types if strict mode and validateTypes are enabled
    if (opts.strict && opts.validateTypes) {
      if (attributes.title && typeof attributes.title !== 'string') {
        warnings.push('title should be a string');
      }
      if (
        attributes.type &&
        !['document', 'phrase'].includes(attributes.type)
      ) {
        warnings.push('type should be "document" or "phrase"');
      }
      if (attributes.tags && !Array.isArray(attributes.tags)) {
        warnings.push('tags should be an array');
      }
    }

    return {
      metadata,
      content: parsed.content.trim(),
      rawContent: content,
      hasFrontmatter: actuallyHasFrontmatter,
      errors: [],
      warnings,
      suggestionDirectives: parseSuggestionDirectives(attributes),
    };
  } catch (error) {
    // For invalid YAML, try to extract what we can
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to parse frontmatter';

    // Check if content looks like it has frontmatter even if YAML is invalid
    const looksLikeFrontmatter =
      content.trimStart().startsWith('---') ||
      !!(
        opts.delimiters &&
        content
          .trimStart()
          .startsWith(
            Array.isArray(opts.delimiters)
              ? opts.delimiters[0]
              : opts.delimiters
          )
      );

    // If there's invalid YAML frontmatter, extract content after the closing delimiter
    let extractedContent = content;
    if (looksLikeFrontmatter) {
      const delimiter = opts.delimiters
        ? Array.isArray(opts.delimiters)
          ? opts.delimiters[0]
          : opts.delimiters
        : '---';
      const parts = content.split(delimiter);
      if (parts.length >= 3) {
        extractedContent = parts.slice(2).join(delimiter).trim();
      }
    }

    return {
      metadata: {
        title: extractTitle(undefined, extractedContent, opts.extractTitle),
        type: opts.defaultType,
        tags: [],
        updated: opts.autoUpdateTimestamp
          ? new Date().toISOString()
          : undefined,
      },
      content: extractedContent,
      rawContent: content,
      hasFrontmatter: looksLikeFrontmatter,
      errors: [errorMessage],
      warnings,
      suggestionDirectives: {}, // Empty directives on parse error
    };
  }
}

/**
 * Serialize metadata and content back to YAML frontmatter format
 */
export function serializeFrontmatter(
  metadata: FileMetadata | Record<string, any>,
  content: string,
  options: FrontmatterOptions | { delimiters?: string } = {}
): string {
  try {
    // Check if metadata is empty or only has undefined values
    const hasContent = Object.values(metadata).some(
      (v) => v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
    );
    if (!hasContent) {
      return content;
    }

    // Prepare frontmatter data, excluding undefined values
    const frontmatterData: Record<string, any> = {};

    if ('title' in metadata && metadata.title)
      frontmatterData.title = metadata.title;
    if ('type' in metadata && metadata.type)
      frontmatterData.type = metadata.type;
    if (
      'tags' in metadata &&
      Array.isArray(metadata.tags) &&
      metadata.tags.length > 0
    )
      frontmatterData.tags = metadata.tags;
    if ('updated' in metadata && metadata.updated)
      frontmatterData.updated = metadata.updated;
    if ('custom' in metadata && metadata.custom)
      Object.assign(frontmatterData, metadata.custom);

    // Also include any other fields not in the standard set
    Object.entries(metadata).forEach(([key, value]) => {
      if (
        !['title', 'type', 'tags', 'updated', 'custom', 'size'].includes(key) &&
        value !== undefined
      ) {
        frontmatterData[key] = value;
      }
    });

    // Apply field mapping if provided
    if ('fieldMapping' in options && options.fieldMapping) {
      Object.entries(options.fieldMapping).forEach(([from, to]) => {
        if (frontmatterData[from] !== undefined) {
          frontmatterData[to] = frontmatterData[from];
          delete frontmatterData[from];
        }
      });
    }

    // Use js-yaml to stringify with proper YAML formatting
    const delimiter =
      typeof options.delimiters === 'string' ? options.delimiters : '---';
    const yamlString = yaml.dump(frontmatterData);
    return `${delimiter}\n${yamlString}${delimiter}\n${content}`.trim();
  } catch (error) {
    return content;
  }
}

/**
 * Update frontmatter in existing content
 */
export function updateFrontmatter(
  content: string,
  updates: Partial<FileMetadata> | Record<string, any>,
  options: FrontmatterOptions = {}
): string {
  const parsed = parseFrontmatter(content, options);

  let updatedMetadata: FileMetadata | Record<string, any>;

  if (options.merge) {
    // Merge mode: keep existing metadata and merge updates
    updatedMetadata = {
      ...parsed.metadata,
      ...updates,
      updated:
        options.autoUpdateTimestamp !== false
          ? new Date().toISOString()
          : parsed.metadata.updated,
    };
  } else {
    // Replace mode: replace with updates
    updatedMetadata = {
      ...parsed.metadata,
      ...updates,
      updated:
        options.autoUpdateTimestamp !== false
          ? new Date().toISOString()
          : parsed.metadata.updated,
    };
  }

  return serializeFrontmatter(updatedMetadata, parsed.content, options);
}

/**
 * Check if content has YAML frontmatter
 */
export function hasFrontmatter(content: string): boolean {
  return content.trimStart().startsWith('---');
}

/**
 * Extract metadata without parsing content (for performance)
 */
export function extractMetadataOnly(
  content: string,
  options: FrontmatterOptions = {}
): FileMetadata {
  const parsed = parseFrontmatter(content, options);
  return parsed.metadata;
}

/**
 * Extract just the frontmatter from content
 */
export function extractFrontmatter(content: string): string | null {
  if (!content.trimStart().startsWith('---')) {
    return null;
  }

  const lines = content.split('\n');
  const endIndex = lines.findIndex((line, i) => i > 0 && line.trim() === '---');

  if (endIndex === -1) {
    return null;
  }

  return lines.slice(1, endIndex).join('\n');
}

/**
 * Remove frontmatter from content
 */
export function removeFrontmatter(content: string): string {
  const parsed = matter(content);
  return parsed.content.trim();
}

/**
 * Stringify metadata back to YAML frontmatter format
 */
export function stringifyFrontmatter(
  metadata: FileMetadata,
  content: string = ''
): string {
  const yamlStr = yaml.dump(metadata, {
    indent: 2,
    lineWidth: 80,
    noRefs: true,
    sortKeys: false,
  });

  return `---\n${yamlStr}---\n${content}`;
}

/**
 * Validate that frontmatter structure is correct
 */
export function validateFrontmatter(metadata: any): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check title
  if (metadata.title !== undefined) {
    if (typeof metadata.title !== 'string') {
      errors.push('title must be a string');
    } else if (metadata.title.trim() === '') {
      errors.push('title cannot be empty');
    }
  }

  // Check type
  if (metadata.type !== undefined) {
    if (!['document', 'phrase'].includes(metadata.type)) {
      errors.push(
        `Invalid type "${metadata.type}". Must be "document" or "phrase".`
      );
    }
  }

  // Check tags format
  if (metadata.tags !== undefined) {
    if (!Array.isArray(metadata.tags)) {
      if (typeof metadata.tags === 'string') {
        warnings.push('Tags should be an array. Converting string to array.');
      } else {
        errors.push('tags must be an array');
      }
    } else {
      metadata.tags.forEach((tag: any, i: number) => {
        if (typeof tag !== 'string' || tag.trim() === '') {
          errors.push(`tags[${i}] must be a non-empty string`);
        }
      });
    }
  }

  // Check date format
  if (metadata.updated !== undefined) {
    const date = new Date(metadata.updated);
    if (isNaN(date.getTime())) {
      errors.push(`Invalid date format for "updated": ${metadata.updated}`);
    }
  }

  // Check for common typos in field names
  const commonTypos: Record<string, string> = {
    titel: 'title',
    tag: 'tags',
    typ: 'type',
    update: 'updated',
  };

  Object.keys(metadata).forEach((key) => {
    if (commonTypos[key]) {
      warnings.push(`Possible typo: "${key}" should be "${commonTypos[key]}"`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Sanitize metadata for safe storage
 */
export function sanitizeMetadata(
  metadata: Partial<FileMetadata> | any
): FileMetadata {
  const sanitized: any = {};

  // Sanitize title
  if (metadata.title !== undefined) {
    const title =
      typeof metadata.title === 'string' ? metadata.title.trim() : '';
    if (title) sanitized.title = title;
  }

  // Sanitize type
  sanitized.type = metadata.type === 'phrase' ? 'phrase' : 'document';

  // Sanitize tags
  sanitized.tags = sanitizeTags(metadata.tags);

  // Sanitize timestamp
  const updated = sanitizeTimestamp(metadata.updated);
  if (updated) sanitized.updated = updated;

  // Sanitize custom fields - but preserve nested structure
  if (metadata.custom && typeof metadata.custom === 'object') {
    const custom = sanitizeCustomObject(metadata.custom);
    if (custom && Object.keys(custom).length > 0) {
      sanitized.custom = custom;
    }
  }

  // Remove any fields that became undefined
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });

  // Handle other non-standard fields that might be in metadata
  const standardFields = ['title', 'type', 'tags', 'updated', 'custom', 'size'];
  Object.keys(metadata).forEach((key) => {
    if (
      !standardFields.includes(key) &&
      metadata[key] !== undefined &&
      metadata[key] !== ''
    ) {
      sanitized[key] = metadata[key];
    }
  });

  // Sanitize size if present
  if (typeof metadata.size === 'number' && metadata.size >= 0) {
    sanitized.size = metadata.size;
  }

  return sanitized;
}

function sanitizeCustomObject(obj: any): any {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return undefined;
  }

  const result: any = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      // Skip empty values
      return;
    }

    if (typeof value === 'string') {
      result[key] = value.trim();
    } else {
      result[key] = value;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

// Helper functions

function extractTitle(
  frontmatterTitle: any,
  content: string,
  extractFromContent: boolean
): string | undefined {
  if (typeof frontmatterTitle === 'string' && frontmatterTitle.trim()) {
    return frontmatterTitle.trim();
  }

  if (extractFromContent) {
    return extractTitleFromContent(content);
  }

  return undefined;
}

function extractTitleFromContent(content: string): string | undefined {
  // Try to find first markdown heading
  const headingMatch = content.match(/^#\s+(.+)$/m);
  if (headingMatch) {
    return headingMatch[1].trim();
  }

  // Try to find first non-empty line that looks like a title
  const lines = content.split('\n');
  const firstNonEmptyLine = lines.find((line) => line.trim().length > 0);

  if (firstNonEmptyLine && firstNonEmptyLine.length < 100) {
    // Skip lines that are clearly not titles
    const trimmed = firstNonEmptyLine.trim();

    // Skip code fences, HTML tags, and other non-title patterns
    if (
      trimmed.startsWith('```') ||
      trimmed.startsWith('---') ||
      trimmed.startsWith('<') ||
      trimmed.startsWith('//') ||
      trimmed.startsWith('/*') ||
      trimmed.startsWith('{') ||
      trimmed.startsWith('[') ||
      /^import\s+/.test(trimmed) ||
      /^export\s+/.test(trimmed) ||
      /^const\s+/.test(trimmed) ||
      /^let\s+/.test(trimmed) ||
      /^var\s+/.test(trimmed) ||
      /^function\s+/.test(trimmed)
    ) {
      return undefined;
    }

    // Remove markdown formatting
    const cleanTitle = trimmed
      .replace(/^#+\s*/, '') // Remove heading markers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove code
      .trim();

    if (cleanTitle.length > 0) {
      return cleanTitle;
    }
  }

  return undefined;
}

function validateFileType(type: any, defaultType: FileType): FileType {
  if (type === 'document' || type === 'phrase') {
    return type;
  }
  return defaultType;
}

function normalizeTags(tags: any): string[] {
  if (Array.isArray(tags)) {
    return tags
      .filter((tag) => typeof tag === 'string')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }

  if (typeof tags === 'string') {
    return tags
      .split(/[,;]/)
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }

  return [];
}

function sanitizeTags(tags: any): string[] {
  const normalized = normalizeTags(tags);
  return normalized.filter((tag) => tag.length > 0).slice(0, 50); // Limit number of tags
}

function normalizeTimestamp(
  timestamp: any,
  autoGenerate: boolean
): string | undefined {
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  }

  if (autoGenerate) {
    return new Date().toISOString();
  }

  return undefined;
}

function sanitizeTimestamp(timestamp: any): string | undefined {
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  }
  return undefined;
}

function extractCustomFields(
  data: Record<string, any>
): Record<string, any> | undefined {
  const standardFields = new Set([
    'title',
    'type',
    'tags',
    'updated',
    'custom',
  ]);
  const custom: Record<string, any> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (!standardFields.has(key)) {
      // Sanitize custom field values
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        custom[key] = value;
      } else if (Array.isArray(value)) {
        custom[key] = value.filter(
          (item) =>
            typeof item === 'string' ||
            typeof item === 'number' ||
            typeof item === 'boolean'
        );
      } else if (value && typeof value === 'object') {
        // Allow nested objects but sanitize them
        custom[key] = sanitizeObject(value);
      }
    }
  });

  return Object.keys(custom).length > 0 ? custom : undefined;
}

function sanitizeObject(obj: any, depth = 0): any {
  if (depth > 3) return null; // Prevent deep nesting

  if (Array.isArray(obj)) {
    return obj
      .slice(0, 50) // Limit array size
      .map((item) => sanitizeObject(item, depth + 1))
      .filter((item) => item !== null);
  }

  if (obj && typeof obj === 'object') {
    const sanitized: any = {};
    Object.entries(obj)
      .slice(0, 20)
      .forEach(([key, value]) => {
        // Limit object size
        if (typeof key === 'string' && key.length < 50) {
          // Limit key length
          const sanitizedValue = sanitizeObject(value, depth + 1);
          if (sanitizedValue !== null) {
            sanitized[key] = sanitizedValue;
          }
        }
      });
    return sanitized;
  }

  if (typeof obj === 'string' && obj.length < 1000) return obj; // Limit string length
  if (typeof obj === 'number' && isFinite(obj)) return obj;
  if (typeof obj === 'boolean') return obj;

  return null;
}

// ============================================================================
// Suggestion Directive Parsing Functions
// ============================================================================

/**
 * Parse suggestion directives from frontmatter data
 */
function parseSuggestionDirectives(
  attributes: Record<string, any>
): SuggestionDirectives {
  const directives: SuggestionDirectives = {};

  // Parse @include-from directives
  const includeFromRaw = attributes['include-from'] || attributes.includeFrom;
  if (includeFromRaw) {
    directives.includeFrom = parseIncludeFromDirectives(includeFromRaw);
  }

  // Parse @exclude-from directives
  const excludeFromRaw = attributes['exclude-from'] || attributes.excludeFrom;
  if (excludeFromRaw) {
    directives.excludeFrom = parseExcludeFromDirectives(excludeFromRaw);
  }

  // Parse @theme directive
  const themeRaw = attributes.theme;
  if (themeRaw) {
    const parsed = parseThemeDirective(themeRaw);
    if (parsed) directives.theme = parsed;
  }

  // Parse @audience directive
  const audienceRaw = attributes.audience;
  if (audienceRaw) {
    const parsed = parseAudienceDirective(audienceRaw);
    if (parsed) directives.audience = parsed;
  }

  // Parse @style directive
  const styleRaw = attributes.style;
  if (styleRaw) {
    const parsed = parseStyleDirective(styleRaw);
    if (parsed) directives.style = parsed;
  }

  return directives;
}

/**
 * Parse @include-from directives
 * Supports both simple and complex formats:
 * - Simple: "tag1,tag2,tag3"
 * - Complex: { tags: ["tag1", "tag2"], weight: "high", priority: 1, mode: "any" }
 * - Array: [{ tags: ["tag1"], weight: "high" }, "tag2,tag3"]
 */
function parseIncludeFromDirectives(raw: any): TagIncludeDirective[] {
  if (!raw) return [];

  const directives: TagIncludeDirective[] = [];

  // Handle array of directives
  if (Array.isArray(raw)) {
    raw.forEach((item, index) => {
      const directive = parseIncludeFromDirective(item, index + 1);
      if (directive) directives.push(directive);
    });
  } else {
    // Handle single directive
    const directive = parseIncludeFromDirective(raw, 1);
    if (directive) directives.push(directive);
  }

  return directives;
}

/**
 * Parse single @include-from directive
 */
function parseIncludeFromDirective(
  raw: any,
  defaultPriority: number
): TagIncludeDirective | null {
  if (!raw) return null;

  let tags: string[] = [];
  let weight: 'low' | 'medium' | 'high' | 'critical' = 'medium';
  let priority = defaultPriority;
  let mode: 'all' | 'any' = 'any';
  let context: string | undefined;

  if (typeof raw === 'string') {
    // Simple string format: "tag1,tag2,tag3"
    tags = raw
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  } else if (typeof raw === 'object' && raw !== null) {
    // Complex object format
    if (Array.isArray(raw.tags)) {
      tags = raw.tags.map(String).filter((tag: string) => tag.length > 0);
    } else if (typeof raw.tags === 'string') {
      tags = raw.tags
        .split(',')
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag.length > 0);
    }

    if (
      typeof raw.weight === 'string' &&
      ['low', 'medium', 'high', 'critical'].includes(raw.weight)
    ) {
      weight = raw.weight;
    }

    if (typeof raw.priority === 'number' && raw.priority > 0) {
      priority = Math.floor(raw.priority);
    }

    if (typeof raw.mode === 'string' && ['all', 'any'].includes(raw.mode)) {
      mode = raw.mode;
    }

    if (typeof raw.context === 'string') {
      context = raw.context.trim() || undefined;
    }
  }

  if (tags.length === 0) return null;

  return {
    tags,
    weight,
    priority,
    mode,
    context,
  };
}

/**
 * Parse @exclude-from directives
 */
function parseExcludeFromDirectives(raw: any): TagExcludeDirective[] {
  if (!raw) return [];

  const directives: TagExcludeDirective[] = [];

  // Handle array of directives
  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const directive = parseExcludeFromDirective(item);
      if (directive) directives.push(directive);
    });
  } else {
    // Handle single directive
    const directive = parseExcludeFromDirective(raw);
    if (directive) directives.push(directive);
  }

  return directives;
}

/**
 * Parse single @exclude-from directive
 */
function parseExcludeFromDirective(raw: any): TagExcludeDirective | null {
  if (!raw) return null;

  let tags: string[] = [];
  let reason: string | undefined;
  let mode: 'hard' | 'soft' = 'soft';

  if (typeof raw === 'string') {
    // Simple string format: "tag1,tag2,tag3"
    tags = raw
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  } else if (typeof raw === 'object' && raw !== null) {
    // Complex object format
    if (Array.isArray(raw.tags)) {
      tags = raw.tags.map(String).filter((tag: string) => tag.length > 0);
    } else if (typeof raw.tags === 'string') {
      tags = raw.tags
        .split(',')
        .map((tag: string) => tag.trim())
        .filter((tag: string) => tag.length > 0);
    }

    if (typeof raw.reason === 'string') {
      reason = raw.reason.trim() || undefined;
    }

    if (typeof raw.mode === 'string' && ['hard', 'soft'].includes(raw.mode)) {
      mode = raw.mode;
    }
  }

  if (tags.length === 0) return null;

  return {
    tags,
    reason,
    mode,
  };
}

/**
 * Parse @theme directive
 */
function parseThemeDirective(raw: any): ThemeDirective | null {
  if (!raw) return null;

  let themes: string[] = [];
  let boost = 1.0;
  let specificity: 'broad' | 'focused' | 'strict' = 'focused';
  let subThemes: string[] | undefined;

  if (typeof raw === 'string') {
    // Simple string format: "theme1,theme2,theme3"
    themes = raw
      .split(',')
      .map((theme) => theme.trim())
      .filter((theme) => theme.length > 0);
  } else if (typeof raw === 'object' && raw !== null) {
    // Complex object format
    if (Array.isArray(raw.themes)) {
      themes = raw.themes
        .map(String)
        .filter((theme: string) => theme.length > 0);
    } else if (typeof raw.themes === 'string') {
      themes = raw.themes
        .split(',')
        .map((theme: string) => theme.trim())
        .filter((theme: string) => theme.length > 0);
    }

    if (typeof raw.boost === 'number' && raw.boost >= 0.1 && raw.boost <= 2.0) {
      boost = raw.boost;
    }

    if (
      typeof raw.specificity === 'string' &&
      ['broad', 'focused', 'strict'].includes(raw.specificity)
    ) {
      specificity = raw.specificity;
    }

    if (Array.isArray(raw.subThemes)) {
      subThemes = raw.subThemes
        .map(String)
        .filter((theme: string) => theme.length > 0);
      if (subThemes && subThemes.length === 0) subThemes = undefined;
    }
  }

  if (themes.length === 0) return null;

  return {
    themes,
    boost,
    specificity,
    subThemes,
  };
}

/**
 * Parse @audience directive
 */
function parseAudienceDirective(raw: any): AudienceDirective | null {
  if (!raw) return null;

  let audienceTypes: string[] = [];
  let complexity: 'beginner' | 'intermediate' | 'advanced' | 'expert' =
    'intermediate';
  let tone: 'formal' | 'casual' | 'friendly' | 'professional' | 'technical' =
    'professional';
  let domainExpertise: string | undefined;
  let formality: 'high' | 'medium' | 'low' = 'medium';

  if (typeof raw === 'string') {
    // Simple string format: "developer,intermediate"
    audienceTypes = raw
      .split(',')
      .map((type) => type.trim())
      .filter((type) => type.length > 0);
  } else if (typeof raw === 'object' && raw !== null) {
    // Complex object format
    if (Array.isArray(raw.audienceTypes)) {
      audienceTypes = raw.audienceTypes
        .map(String)
        .filter((type: string) => type.length > 0);
    } else if (typeof raw.audienceTypes === 'string') {
      audienceTypes = raw.audienceTypes
        .split(',')
        .map((type: string) => type.trim())
        .filter((type: string) => type.length > 0);
    }

    if (
      typeof raw.complexity === 'string' &&
      ['beginner', 'intermediate', 'advanced', 'expert'].includes(
        raw.complexity
      )
    ) {
      complexity = raw.complexity;
    }

    if (
      typeof raw.tone === 'string' &&
      ['formal', 'casual', 'friendly', 'professional', 'technical'].includes(
        raw.tone
      )
    ) {
      tone = raw.tone;
    }

    if (typeof raw.domainExpertise === 'string') {
      domainExpertise = raw.domainExpertise.trim() || undefined;
    }

    if (
      typeof raw.formality === 'string' &&
      ['high', 'medium', 'low'].includes(raw.formality)
    ) {
      formality = raw.formality;
    }
  }

  if (audienceTypes.length === 0) return null;

  return {
    audienceTypes,
    complexity,
    tone,
    domainExpertise,
    formality,
  };
}

/**
 * Parse @style directive
 */
function parseStyleDirective(raw: any): StyleDirective | null {
  if (!raw) return null;

  let characteristics: string[] = [];
  let format:
    | 'prose'
    | 'bullets'
    | 'structured'
    | 'narrative'
    | 'instructional' = 'prose';
  let voice: 'active' | 'passive' | 'mixed' = 'active';
  let lengthPreference: 'concise' | 'moderate' | 'detailed' = 'moderate';
  let structure: StyleDirective['structure'] | undefined;

  if (typeof raw === 'string') {
    // Simple string format: "concise,structured,active"
    characteristics = raw
      .split(',')
      .map((char) => char.trim())
      .filter((char) => char.length > 0);
  } else if (typeof raw === 'object' && raw !== null) {
    // Complex object format
    if (Array.isArray(raw.characteristics)) {
      characteristics = raw.characteristics
        .map(String)
        .filter((char: string) => char.length > 0);
    } else if (typeof raw.characteristics === 'string') {
      characteristics = raw.characteristics
        .split(',')
        .map((char: string) => char.trim())
        .filter((char: string) => char.length > 0);
    }

    if (
      typeof raw.format === 'string' &&
      ['prose', 'bullets', 'structured', 'narrative', 'instructional'].includes(
        raw.format
      )
    ) {
      format = raw.format;
    }

    if (
      typeof raw.voice === 'string' &&
      ['active', 'passive', 'mixed'].includes(raw.voice)
    ) {
      voice = raw.voice;
    }

    if (
      typeof raw.lengthPreference === 'string' &&
      ['concise', 'moderate', 'detailed'].includes(raw.lengthPreference)
    ) {
      lengthPreference = raw.lengthPreference;
    }

    if (typeof raw.structure === 'object' && raw.structure !== null) {
      structure = {
        useHeadings: Boolean(raw.structure.useHeadings),
        useBullets: Boolean(raw.structure.useBullets),
        useNumbering: Boolean(raw.structure.useNumbering),
        useExamples: Boolean(raw.structure.useExamples),
      };
    }
  }

  if (characteristics.length === 0) return null;

  return {
    characteristics,
    format,
    voice,
    lengthPreference,
    structure,
  };
}

// Pre-defined frontmatter templates
export const FRONTMATTER_TEMPLATES = {
  document: {
    title: '',
    type: 'document' as const,
    tags: [],
    updated: new Date().toISOString(),
  },
  phrase: {
    title: '',
    type: 'phrase' as const,
    tags: [],
    category: '',
    updated: new Date().toISOString(),
  },
} as const;

/**
 * Generate frontmatter template for new files
 */
export function generateFrontmatterTemplate(
  type: FileType,
  title?: string,
  tags?: string[]
): string {
  const template = {
    ...FRONTMATTER_TEMPLATES[type],
    ...(title && { title }),
    ...(tags && tags.length > 0 && { tags }),
  };

  const yamlString = yaml.dump(template);
  return `---\n${yamlString}---\n`;
}
